/**
 * Next.js API is proxied at /api/* on orderbyte.io (port 9013).
 * When that process is down nginx returns HTML 502 — try same-origin,
 * then env base, then the Flask pizzacoupon fallback (always up).
 */

const HTML_ERROR =
  'Cards API is down (nginx 502 / Next.js port 9013). Backup Flask route was used or is unavailable.';

export function listNextApiBases() {
  const bases = [];
  const push = (value) => {
    const normalized = String(value || '').replace(/\/+$/, '');
    if (normalized && !bases.includes(normalized)) {
      bases.push(normalized);
    }
  };

  const env = String(process.env.REACT_APP_NEXT_API_BASE || '').replace(
    /\/+$/,
    ''
  );
  let hostname = '';
  let origin = '';
  if (typeof window !== 'undefined' && window.location) {
    hostname = String(window.location.hostname || '').toLowerCase();
    origin = String(window.location.origin || '').replace(/\/+$/, '');
  }
  const onLocal =
    !hostname || hostname === 'localhost' || hostname === '127.0.0.1';
  const envIsLocal = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(env);

  if (!onLocal && origin) {
    push(origin);
  }
  if (env && !(envIsLocal && !onLocal)) {
    push(env);
  }
  if (onLocal) {
    push(env || 'http://localhost:9013');
  }
  return bases;
}

export function flaskRedemptionApiBase() {
  if (typeof window === 'undefined' || !window.location) {
    return '';
  }
  const hostname = String(window.location.hostname || '').toLowerCase();
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '';
  }
  return `${String(window.location.origin).replace(/\/+$/, '')}/api/pizzacoupon/v1/api`;
}

export function flaskPathForNextPath(nextPathAndQuery) {
  const raw = String(nextPathAndQuery || '');
  const qIdx = raw.indexOf('?');
  const pathname = qIdx >= 0 ? raw.slice(0, qIdx) : raw;
  const search = qIdx >= 0 ? raw.slice(qIdx) : '';
  if (pathname.includes('/admin/cards-program/overview')) {
    return `/user/cards-admin/overview${search}`;
  }
  if (pathname.includes('/admin/cards-program/customers') &&
      !pathname.includes('/customers/')) {
    return `/user/cards-admin/customers${search}`;
  }
  if (pathname.includes('/setup-form/merchant-cards-tab')) {
    return `/user/merchant-cards-tab${search}`;
  }
  return '';
}

export function isHtmlBody(text) {
  return String(text || '')
    .trimStart()
    .startsWith('<');
}

export async function parseJsonOrExplainHtml(response) {
  const text = await response.text();
  if (isHtmlBody(text)) {
    return { ok: false, data: null, error: HTML_ERROR, html: true };
  }
  try {
    return { ok: true, data: JSON.parse(text), error: null, html: false };
  } catch {
    return {
      ok: false,
      data: null,
      error: 'Invalid JSON from API.',
      html: false,
    };
  }
}

async function fetchOnce(url, options = {}, timeoutMs = 120000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(id);
  }
}

/**
 * GET/POST a Next `/api/...` path. Skips HTML 502 bodies and falls back to Flask.
 */
export async function fetchNextJson(pathAndQuery, options = {}) {
  const path = pathAndQuery.startsWith('/')
    ? pathAndQuery
    : `/${pathAndQuery}`;
  const apiKey = process.env.REACT_APP_NEXT_API_KEY || '';
  const headers = {
    ...(options.headers || {}),
  };
  if (apiKey && !headers['X-API-Key']) {
    headers['X-API-Key'] = apiKey;
  }

  let last = {
    ok: false,
    data: null,
    error: 'No API base available.',
    html: false,
  };

  for (const base of listNextApiBases()) {
    try {
      const response = await fetchOnce(`${base}${path}`, {
        ...options,
        headers,
      });
      last = await parseJsonOrExplainHtml(response);
      if (!last.html && last.data) {
        return last;
      }
    } catch (error) {
      last = {
        ok: false,
        data: null,
        error:
          error?.name === 'AbortError'
            ? 'Request timed out.'
            : error?.message || 'Failed to reach Cards API.',
        html: false,
      };
    }
  }

  const flaskBase = flaskRedemptionApiBase();
  const flaskPath = flaskPathForNextPath(path);
  if (flaskBase && flaskPath) {
    try {
      const response = await fetchOnce(`${flaskBase}${flaskPath}`, {
        ...options,
        headers,
      });
      last = await parseJsonOrExplainHtml(response);
    } catch (error) {
      last = {
        ok: false,
        data: null,
        error: error?.message || 'Flask backup API failed.',
        html: false,
      };
    }
  }

  return last;
}

export function getDefaultNextApiBase() {
  return listNextApiBases()[0] || 'http://localhost:9013';
}
